import { useState, useEffect, useRef } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { ensureCsvPassword } from "@/lib/csvPassword";
import { parseAddress } from "@/lib/parseAddress";
import { Factory, Plus, Search, Download, Upload, Trash2, FileText, FileDown, ChevronDown, Sparkles, Receipt, IdCard, Building2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function PowerPlants({ user, onLogout }) {
  const [powerplants, setPowerplants] = useState([]);
  const [loading, setLoading] = useState(true);
  const isRegistrationOnly = user?.role === 'powerplant_registration';
  const [showDialog, setShowDialog] = useState(false);
  const [distributorsList, setDistributorsList] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState("");

  useEffect(() => {
    axiosInstance.get("/distributor-settings").then((res) => {
      const seen = new Set();
      const unique = [];
      (Array.isArray(res.data) ? res.data : []).forEach((d) => {
        const n = (d.distributor || "").trim();
        if (n && !seen.has(n.toUpperCase())) { seen.add(n.toUpperCase()); unique.push(n); }
      });
      unique.sort((a, b) => a.localeCompare(b, "pt-BR"));
      setDistributorsList(unique);
    }).catch(() => {});
  }, []);
  const [editingPlant, setEditingPlant] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [connectionType, setConnectionType] = useState("GD1");
  const [adhesionCostType, setAdhesionCostType] = useState("");
  const itemsPerPage = 10;

  // States for auto-calculation
  const [averageEnergy, setAverageEnergy] = useState("");
  const [tariffValue, setTariffValue] = useState("");
  const [fioBValue, setFioBValue] = useState("");
  const [landRentValue, setLandRentValue] = useState("0,00");
  const [equipmentRentValue, setEquipmentRentValue] = useState("0,00");

  const fileInputRef = useRef(null);

  // IA — preenchimento automático do cadastro de usina
  const formRef = useRef(null);
  const aiInvoiceRef = useRef(null);
  const aiIdRef = useRef(null);
  const aiCnpjRef = useRef(null);
  const [regType, setRegType] = useState("PF");
  const [aiBusy, setAiBusy] = useState("");
  const [aiFilled, setAiFilled] = useState([]);
  const [documents, setDocuments] = useState({ rg_cpf_file: null, energy_bill_file: null, cnpj_card_file: null });
  const AI_DOC_KEY = { "/cooperatives/ai/extract-invoice": "energy_bill_file", "/cooperatives/ai/extract-id-document": "rg_cpf_file", "/cooperatives/ai/extract-cnpj-card": "cnpj_card_file" };

  const setField = (name, value) => {
    const el = formRef.current?.elements?.namedItem(name);
    if (!el || value === null || value === undefined || value === "") return false;
    el.value = String(value);
    return true;
  };

  const setAddressFields = (prefix, addressFull, parts) => {
    const parsed = parseAddress(addressFull);
    const a = { ...parsed, ...Object.fromEntries(Object.entries(parts || {}).filter(([, v]) => v)) };
    const filled = [];
    [["street", a.street], ["number", a.number], ["complement", a.complement], ["neighborhood", a.neighborhood],
     ["city", a.city], ["state", a.state], ["zip_code", a.zip_code]].forEach(([k, v]) => {
      if (setField(`${prefix}_${k}`, v)) filled.push(`${prefix}_${k}`);
    });
    return filled;
  };

  const runAi = async (e, endpoint, apply, label) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Arquivo excede 15 MB."); e.target.value = ""; return; }
    setAiBusy(endpoint);
    const docKey = AI_DOC_KEY[endpoint];
    if (docKey) setDocuments((prev) => ({ ...prev, [docKey]: file }));
    const tid = toast.loading(`Lendo ${label} com IA…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await axiosInstance.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const ex = resp?.data?.extracted || resp?.data || {};
      const filled = apply(ex);
      setAiFilled((prev) => Array.from(new Set([...prev, ...filled])));
      toast.success(`IA preencheu ${filled.length} campo(s) a partir de ${label}.`, { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Erro ao ler ${label}`, { id: tid });
    } finally {
      setAiBusy("");
      e.target.value = "";
    }
  };

  // Fatura da Usina → UC geradora, distribuidora, endereço da usina (PF: proprietário = mesmo endereço)
  const handleAiInvoice = (e) => runAi(e, "/cooperatives/ai/extract-invoice", (ex) => {
    const filled = [];
    if (setField("generator_installation_number", ex.installation_number)) filled.push("generator_installation_number");
    const aiDist = (ex.distributor || "").toUpperCase().trim();
    if (aiDist) {
      const match = distributorsList.find((d) => d.toUpperCase() === aiDist || aiDist.includes(d.toUpperCase()) || d.toUpperCase().includes(aiDist));
      setSelectedDistributor(match || aiDist);
      filled.push("distributor");
    }
    filled.push(...setAddressFields("plant", ex.address_full, ex.address_parts));
    const plantNameEl = formRef.current?.elements?.namedItem("plant_name");
    if (plantNameEl && !plantNameEl.value && ex.cooperative_name) { plantNameEl.value = `UFV ${ex.cooperative_name}`; filled.push("plant_name"); }
    if (regType === "PF") {
      filled.push(...setAddressFields("owner", ex.address_full, ex.address_parts));
      const ownerEl = formRef.current?.elements?.namedItem("owner_name");
      if (ownerEl && !ownerEl.value && setField("owner_name", ex.cooperative_name)) filled.push("owner_name");
      const docEl = formRef.current?.elements?.namedItem("cpf_cnpj");
      if (docEl && !docEl.value && setField("cpf_cnpj", ex.cpf_cnpj)) filled.push("cpf_cnpj");
      if (setField("phone", ex.phone)) filled.push("phone");
      if (setField("email", ex.email)) filled.push("email");
    }
    return filled;
  }, "a fatura da usina");

  // RG/CPF → PF: proprietário; PJ: representante legal
  const handleAiIdDoc = (e) => runAi(e, "/cooperatives/ai/extract-id-document", (ex) => {
    const filled = [];
    if (regType === "PJ") {
      if (setField("legal_representative_name", ex.name)) filled.push("legal_representative_name");
      if (setField("legal_representative_cpf", ex.cpf)) filled.push("legal_representative_cpf");
    } else {
      if (setField("owner_name", ex.name)) filled.push("owner_name");
      if (setField("cpf_cnpj", ex.cpf)) filled.push("cpf_cnpj");
    }
    if (setField("nationality", ex.nationality)) filled.push("nationality");
    if (setField("marital_status", ex.marital_status)) filled.push("marital_status");
    if (setField("profession", ex.profession)) filled.push("profession");
    return filled;
  }, "o documento com foto");

  // Cartão CNPJ (PJ) → razão social, CNPJ, contato e endereço do proprietário
  const handleAiCnpj = (e) => runAi(e, "/cooperatives/ai/extract-cnpj-card", (ex) => {
    const filled = [];
    if (setField("owner_name", ex.corporate_name)) filled.push("owner_name");
    if (setField("cpf_cnpj", ex.cnpj)) filled.push("cpf_cnpj");
    if (setField("phone", ex.phone)) filled.push("phone");
    if (setField("email", ex.email)) filled.push("email");
    filled.push(...setAddressFields("owner", ex.address_full, ex.address_parts));
    return filled;
  }, "o Cartão CNPJ");

  // Calculate rent values when inputs change
  useEffect(() => {
    const avgEnergy = parseFloat(averageEnergy.toString().replace(",", ".")) || 0;
    const tariff = parseFloat(tariffValue.toString().replace(",", ".")) || 0;
    const fioB = connectionType === "GD2" ? (parseFloat(fioBValue.toString().replace(",", ".")) || 0) : 0;
    
    // Valor Locação Terras = Energia Média * (Valor da Tarifa - Valor Fio B) * 25%
    const landRent = avgEnergy * (tariff - fioB) * 0.25;
    // Valor Locação Equipamentos = Energia Média * (Valor da Tarifa - Valor Fio B) * 75%
    const equipmentRent = avgEnergy * (tariff - fioB) * 0.75;
    
    setLandRentValue(landRent.toFixed(2).replace(".", ","));
    setEquipmentRentValue(equipmentRent.toFixed(2).replace(".", ","));
  }, [averageEnergy, tariffValue, fioBValue, connectionType]);

  // Reset form values when opening dialog
  const resetFormValues = (plant = null) => {
    setRegType(plant?.registration_type || "PF");
    setAiFilled([]);
    if (plant) {
      setConnectionType(plant.connection_type || "GD1");
      setAdhesionCostType(plant.adhesion_cost_type || "");
      setSelectedDistributor(plant.distributor || "");
      setAverageEnergy(plant.average_energy?.toString().replace(".", ",") || "");
      setTariffValue(plant.tariff_value?.toString().replace(".", ",") || "");
      setFioBValue(plant.fio_b_value?.toString().replace(".", ",") || "");
      setLandRentValue(plant.land_rent_value?.toString().replace(".", ",") || "0,00");
      setEquipmentRentValue(plant.equipment_rent_value?.toString().replace(".", ",") || "0,00");
    } else {
      setConnectionType("GD1");
      setAdhesionCostType("");
      setAverageEnergy("");
      setTariffValue("");
      setFioBValue("");
      setLandRentValue("0,00");
      setEquipmentRentValue("0,00");
      setSelectedDistributor("");
    }
  };

  useEffect(() => {
    if (user?.role === "admin") {
      fetchPowerPlants();
    } else if (user?.role === "powerplant_registration") {
      setLoading(false);
    }
  }, [user]);

  const fetchPowerPlants = async () => {
    try {
      const response = await axiosInstance.get("/powerplants");
      setPowerplants(response.data);
    } catch (error) {
      toast.error("Erro ao carregar usinas");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const connType = connectionType;
    
    // Parse values for calculation
    const avgEnergy = parseFloat(averageEnergy.toString().replace(",", ".")) || 0;
    const tariff = parseFloat(tariffValue.toString().replace(",", ".")) || 0;
    const fioB = connType === "GD2" ? (parseFloat(fioBValue.toString().replace(",", ".")) || 0) : 0;
    
    // Calculate rent values
    const landRent = avgEnergy * (tariff - fioB) * 0.25;
    const equipmentRent = avgEnergy * (tariff - fioB) * 0.75;
    
    const data = {
      registration_type: formData.get("registration_type"),
      plant_name: formData.get("plant_name"),
      owner_name: formData.get("owner_name"),
      cpf_cnpj: formData.get("cpf_cnpj"),
      phone: formData.get("phone"),
      email: formData.get("email"),
      legal_representative_name: formData.get("legal_representative_name"),
      legal_representative_cpf: formData.get("legal_representative_cpf"),
      owner_street: formData.get("owner_street"),
      owner_number: formData.get("owner_number"),
      owner_neighborhood: formData.get("owner_neighborhood"),
      owner_complement: formData.get("owner_complement"),
      owner_city: formData.get("owner_city"),
      owner_state: formData.get("owner_state"),
      owner_zip_code: formData.get("owner_zip_code"),
      nationality: formData.get("nationality"),
      marital_status: formData.get("marital_status"),
      profession: formData.get("profession"),
      distributor: selectedDistributor || formData.get("distributor") || "",
      generator_installation_number: formData.get("generator_installation_number"),
      connection_type: connType,
      operation_date: formData.get("operation_date") || null,
      distributor_status: formData.get("distributor_status") || "PENDING",
      installed_power: parseFloat(formData.get("installed_power")) || 0,
      average_energy: avgEnergy,
      tariff_value: tariff,
      fio_b: formData.get("fio_b") || "NÃO",
      fio_b_value: fioB,
      land_rent_value: landRent,
      equipment_rent_value: equipmentRent,
      bank_details: formData.get("bank_details"),
      apportionment_status: formData.get("apportionment_status"),
      plant_street: formData.get("plant_street"),
      plant_number: formData.get("plant_number"),
      plant_neighborhood: formData.get("plant_neighborhood"),
      plant_complement: formData.get("plant_complement"),
      plant_city: formData.get("plant_city"),
      plant_state: formData.get("plant_state"),
      plant_zip_code: formData.get("plant_zip_code"),
      registration_number: formData.get("registration_number"),
      signature_date: formData.get("signature_date") || null,
      ownership_transfer_date: formData.get("ownership_transfer_date") || null,
      first_compensation_month: (formData.get("first_compensation_month") || "").toUpperCase() || null,
      payment_type: formData.get("payment_type") || null,
      adhesion_cost_type: formData.get("adhesion_cost_type") || null,
      cac_number: formData.get("adhesion_cost_type") === "Com CAC"
        ? (parseFloat((formData.get("cac_number") || "").toString().replace(",", ".")) || 0)
        : null,
    };

    if (!editingPlant) {
      if (!documents.rg_cpf_file) { toast.error("Anexe o documento RG/CPF"); return; }
      if (!documents.energy_bill_file) { toast.error("Anexe a conta de energia da usina"); return; }
      if (data.registration_type === "PJ" && !documents.cnpj_card_file) { toast.error("Anexe o Cartão CNPJ (obrigatório para PJ)"); return; }
    }
    if (data.registration_type !== "PJ") {
      data.legal_representative_name = null;
      data.legal_representative_cpf = null;
    }

    try {
      if (editingPlant) {
        await axiosInstance.put(`/powerplants/${editingPlant.id}`, { ...data, id: editingPlant.id });
        toast.success("Usina atualizada com sucesso!");
      } else {
        const fd = new FormData();
        fd.append("payload", JSON.stringify(data));
        fd.append("rg_cpf_file", documents.rg_cpf_file);
        fd.append("energy_bill_file", documents.energy_bill_file);
        if (documents.cnpj_card_file) fd.append("cnpj_card_file", documents.cnpj_card_file);
        await axiosInstance.post("/powerplants/register", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Usina cadastrada com sucesso!");
      }
      setDocuments({ rg_cpf_file: null, energy_bill_file: null, cnpj_card_file: null });
      if (isRegistrationOnly) {
        formRef.current?.reset();
        resetFormValues(null);
        setAiFilled([]);
        return;
      }
      setShowDialog(false);
      setEditingPlant(null);
      fetchPowerPlants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar usina");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Tem certeza que deseja excluir esta usina?")) return;
    
    try {
      await axiosInstance.delete(`/powerplants/${id}`);
      toast.success("Usina excluída com sucesso!");
      fetchPowerPlants();
    } catch (error) {
      toast.error("Erro ao excluir usina");
    }
  };

  const CONTRACT_PARTS = [
    { key: "all", label: "Contrato Completo (Equipamentos + Imóvel + Procuração)" },
    { key: "equipamentos", label: "Contrato de Locação de Equipamentos" },
    { key: "imovel", label: "Contrato de Locação de Imóvel" },
    { key: "procuracao", label: "Procuração" },
  ];

  const handleDownloadDocument = async (plant, docType) => {
    try {
      const resp = await axiosInstance.get(`/powerplants/${plant.id}/documents/${docType}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `${docType}_${plant.plant_name || "usina"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao baixar documento");
    }
  };

  const handleDownloadContract = async (plant, format = "pdf", part = "all") => {
    const isPdf = format === "pdf";
    try {
      const resp = await axiosInstance.get(
        `/powerplants/${plant.id}/contract-${isPdf ? "pdf" : "docx"}`,
        { params: { part }, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], isPdf ? { type: "application/pdf" } : undefined));
      const a = document.createElement("a");
      a.href = url;
      const safe = (plant.plant_name || "usina").replace(/\s+/g, "_").replace(/\//g, "-");
      const suffix = part === "all" ? "" : `_${part}`;
      a.download = `contrato_usina${suffix}_${safe}.${isPdf ? "pdf" : "docx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erro ao gerar contrato ${isPdf ? "PDF" : "Word"}`);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axiosInstance.post("/powerplants/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} erros encontrados`);
      }
      fetchPowerPlants();
    } catch (error) {
      toast.error("Erro ao importar CSV");
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleExportCSV = async () => {
    if (!ensureCsvPassword()) return;
    try {
      const response = await axiosInstance.get("/powerplants/export-csv", {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "usinas.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSV exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar CSV");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("/powerplants/template", {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "modelo_usinas.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Modelo baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar modelo");
    }
  };

  const handleDownloadWordTemplate = async () => {
    try {
      const response = await axiosInstance.get("/powerplants/fields-template-docx", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Template_Contrato_Usina_Fields.docx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Modelo Word baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar modelo Word");
    }
  };

  // Filter and paginate
  const filteredPlants = powerplants.filter(plant =>
    plant.plant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plant.owner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plant.generator_installation_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPlants.length / itemsPerPage);
  const paginatedPlants = filteredPlants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const powerplantForm = (
            <form onSubmit={handleSubmit} className="space-y-6" ref={formRef}>
              {/* IA — preenchimento automático */}
              <div className="rounded-lg border border-purple-200 bg-purple-50/60 p-4" data-testid="powerplant-ai-box">
                <div className="flex items-center gap-2 text-sm font-semibold text-purple-800">
                  <Sparkles className="w-4 h-4" /> Preenchimento automático com IA
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Gemini 3 Flash</span>
                </div>
                <p className="text-xs text-purple-900/80 mt-1">
                  {regType === "PJ"
                    ? "PJ: envie o Cartão CNPJ (proprietário + endereço do proprietário), a fatura da usina (UC, distribuidora e endereço da usina) e o RG/CNH do representante legal."
                    : "PF: envie a fatura da usina (UC, distribuidora e endereço — usado também como endereço do proprietário) e o RG/CNH do proprietário."}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <input ref={aiInvoiceRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={handleAiInvoice} data-testid="powerplant-ai-invoice-input" />
                  <input ref={aiIdRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={handleAiIdDoc} data-testid="powerplant-ai-id-input" />
                  <input ref={aiCnpjRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={handleAiCnpj} data-testid="powerplant-ai-cnpj-input" />
                  {regType === "PJ" && (
                    <Button type="button" variant="outline" size="sm" className="border-purple-300 text-purple-800 bg-white" disabled={!!aiBusy}
                      onClick={() => aiCnpjRef.current?.click()} data-testid="powerplant-ai-cnpj-btn">
                      <Building2 className="w-4 h-4 mr-1" /> Ler Cartão CNPJ
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" className="border-purple-300 text-purple-800 bg-white" disabled={!!aiBusy}
                    onClick={() => aiInvoiceRef.current?.click()} data-testid="powerplant-ai-invoice-btn">
                    <Receipt className="w-4 h-4 mr-1" /> Ler Fatura da Usina
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="border-purple-300 text-purple-800 bg-white" disabled={!!aiBusy}
                    onClick={() => aiIdRef.current?.click()} data-testid="powerplant-ai-id-btn">
                    <IdCard className="w-4 h-4 mr-1" /> {regType === "PJ" ? "Ler RG/CPF do Representante" : "Ler RG/CPF"}
                  </Button>
                  {aiBusy && <span className="text-xs text-purple-700 self-center">Lendo…</span>}
                </div>
                {aiFilled.length > 0 && (
                  <p className="text-[11px] text-purple-700 mt-2" data-testid="powerplant-ai-filled">✨ IA preencheu {aiFilled.length} campo(s) — revise antes de salvar.</p>
                )}
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de Cadastro *</Label>
                  <select name="registration_type" defaultValue={editingPlant?.registration_type || "PF"} onChange={(e) => setRegType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md" required data-testid="powerplant-registration-type">
                    <option value="PF">PF</option>
                    <option value="PJ">PJ</option>
                  </select>
                </div>
                <div>
                  <Label>Nome da Usina *</Label>
                  <Input name="plant_name" defaultValue={editingPlant?.plant_name} required />
                </div>
                <div>
                  <Label>Proprietário *</Label>
                  <Input name="owner_name" defaultValue={editingPlant?.owner_name} required />
                </div>
                <div>
                  <Label>CPF/CNPJ *</Label>
                  <Input name="cpf_cnpj" defaultValue={editingPlant?.cpf_cnpj} required />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <Input name="phone" defaultValue={editingPlant?.phone} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input name="email" type="email" defaultValue={editingPlant?.email} />
                </div>
              </div>

              {/* Legal Representative (PJ) */}
              {regType === "PJ" && (
              <div className="grid grid-cols-2 gap-4" data-testid="legal-representative-block">
                <div>
                  <Label>Nome do Representante Legal *</Label>
                  <Input name="legal_representative_name" defaultValue={editingPlant?.legal_representative_name} required />
                </div>
                <div>
                  <Label>CPF do Representante Legal *</Label>
                  <Input name="legal_representative_cpf" defaultValue={editingPlant?.legal_representative_cpf} required />
                </div>
              </div>
              )}

              {/* Documentos (obrigatórios no cadastro — mesmos moldes de Parceiros) */}
              {!editingPlant ? (
              <div className="border-t pt-4" data-testid="powerplant-documents-block">
                <h3 className="font-semibold mb-2">Documentos <span className="text-red-500">*</span></h3>
                <p className="text-xs text-gray-500 mb-3">Formatos aceitos: PDF, JPG, PNG. Arquivos lidos pela IA acima já ficam anexados automaticamente.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label>RG/CPF {regType === "PJ" ? "do Representante" : ""} <span className="text-red-500">*</span></Label>
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDocuments({ ...documents, rg_cpf_file: e.target.files[0] || null })} data-testid="doc-rg-cpf" />
                    {documents.rg_cpf_file && <p className="text-[11px] text-emerald-700 truncate">Anexado: {documents.rg_cpf_file.name}</p>}
                  </div>
                  <div className="space-y-1">
                    <Label>Conta de Energia da Usina <span className="text-red-500">*</span></Label>
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDocuments({ ...documents, energy_bill_file: e.target.files[0] || null })} data-testid="doc-energy-bill" />
                    {documents.energy_bill_file && <p className="text-[11px] text-emerald-700 truncate">Anexado: {documents.energy_bill_file.name}</p>}
                  </div>
                  {regType === "PJ" && (
                    <div className="space-y-1">
                      <Label>Cartão CNPJ <span className="text-red-500">*</span></Label>
                      <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDocuments({ ...documents, cnpj_card_file: e.target.files[0] || null })} data-testid="doc-cnpj-card" />
                      {documents.cnpj_card_file && <p className="text-[11px] text-emerald-700 truncate">Anexado: {documents.cnpj_card_file.name}</p>}
                    </div>
                  )}
                </div>
              </div>
              ) : (editingPlant?.documents_uploaded?.length > 0 && (
                <div className="border-t pt-4" data-testid="powerplant-documents-links">
                  <h3 className="font-semibold mb-2">Documentos anexados</h3>
                  <div className="flex flex-wrap gap-2">
                    {editingPlant.documents_uploaded.map((d) => (
                      <Button key={d} type="button" variant="outline" size="sm" onClick={() => handleDownloadDocument(editingPlant, d)} data-testid={`download-doc-${d}`}>
                        <Download className="w-3 h-3 mr-1" /> {{ rg_cpf: "RG/CPF", energy_bill: "Conta de Energia", cnpj_card: "Cartão CNPJ" }[d]}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Owner Address */}
              <div>
                <h3 className="font-semibold mb-2">Endereço do Proprietário</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Rua</Label>
                    <Input name="owner_street" defaultValue={editingPlant?.owner_street} />
                  </div>
                  <div>
                    <Label>Número</Label>
                    <Input name="owner_number" defaultValue={editingPlant?.owner_number} />
                  </div>
                  <div>
                    <Label>Bairro</Label>
                    <Input name="owner_neighborhood" defaultValue={editingPlant?.owner_neighborhood} />
                  </div>
                  <div>
                    <Label>Complemento</Label>
                    <Input name="owner_complement" defaultValue={editingPlant?.owner_complement} />
                  </div>
                  <div>
                    <Label>Cidade</Label>
                    <Input name="owner_city" defaultValue={editingPlant?.owner_city} />
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Input name="owner_state" defaultValue={editingPlant?.owner_state} />
                  </div>
                  <div>
                    <Label>CEP</Label>
                    <Input name="owner_zip_code" defaultValue={editingPlant?.owner_zip_code} />
                  </div>
                </div>
              </div>

              {/* Personal Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Nacionalidade</Label>
                  <Input name="nationality" defaultValue={editingPlant?.nationality} />
                </div>
                <div>
                  <Label>Estado Civil</Label>
                  <Input name="marital_status" defaultValue={editingPlant?.marital_status} />
                </div>
                <div>
                  <Label>Profissão</Label>
                  <Input name="profession" defaultValue={editingPlant?.profession} />
                </div>
              </div>

              {/* Technical Info */}
              <div>
                <h3 className="font-semibold mb-2">Dados Técnicos</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Distribuidora</Label>
                    <select
                      name="distributor"
                      value={selectedDistributor}
                      onChange={(e) => setSelectedDistributor(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      data-testid="form-distributor"
                    >
                      <option value="">Selecione a distribuidora</option>
                      {selectedDistributor && !distributorsList.some((d) => d.toUpperCase() === selectedDistributor.toUpperCase()) && (
                        <option value={selectedDistributor}>{selectedDistributor}</option>
                      )}
                      {distributorsList.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Nº Instalação do Gerador</Label>
                    <Input name="generator_installation_number" defaultValue={editingPlant?.generator_installation_number} />
                  </div>
                  <div>
                    <Label>Tipo de Conexão</Label>
                    <select 
                      name="connection_type" 
                      value={connectionType}
                      onChange={(e) => setConnectionType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="GD1">GD1</option>
                      <option value="GD2">GD2</option>
                    </select>
                  </div>
                  {connectionType === "GD2" && !isRegistrationOnly && (
                    <div>
                      <Label>Valor do Fio B (R$)</Label>
                      <Input 
                        name="fio_b_value" 
                        type="text" 
                        placeholder="Ex: 0,25"
                        value={fioBValue}
                        onChange={(e) => setFioBValue(e.target.value)}
                      />
                    </div>
                  )}
                  <div>
                    <Label>Data de Operação</Label>
                    <Input name="operation_date" type="date" defaultValue={editingPlant?.operation_date?.split('T')[0]} />
                  </div>
                  {!isRegistrationOnly && (
                  <div>
                    <Label>Status na Distribuidora</Label>
                    <select name="distributor_status" defaultValue={editingPlant?.distributor_status || "PENDING"} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                      <option value="PENDING">PENDING</option>
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </div>
                  )}
                  <div>
                    <Label>Potência Instalada (kW)</Label>
                    <Input name="installed_power" type="number" step="0.01" defaultValue={editingPlant?.installed_power} />
                  </div>
                  <div>
                    <Label>Energia Média (kWh)</Label>
                    <Input 
                      name="average_energy" 
                      type="text" 
                      value={averageEnergy}
                      onChange={(e) => setAverageEnergy(e.target.value)}
                      placeholder="Ex: 15000"
                    />
                  </div>
                  {!isRegistrationOnly && (<>
                  <div>
                    <Label>Valor da Tarifa (R$)</Label>
                    <Input 
                      name="tariff_value" 
                      type="text" 
                      value={tariffValue}
                      onChange={(e) => setTariffValue(e.target.value)}
                      placeholder="Ex: 0,85"
                    />
                  </div>
                  <div>
                    <Label>Fio B</Label>
                    <select name="fio_b" defaultValue={editingPlant?.fio_b || "NÃO"} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                      <option value="SIM">SIM</option>
                      <option value="NÃO">NÃO</option>
                    </select>
                  </div>
                  </>)}
                </div>
              </div>

              {/* Financial - Auto-calculated */}
              {isRegistrationOnly ? (
                <div>
                  <Label>Dados Bancários</Label>
                  <Input name="bank_details" defaultValue={editingPlant?.bank_details} />
                </div>
              ) : (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-800 mb-3">Valores Calculados Automaticamente</h3>
                <p className="text-xs text-blue-600 mb-3">Fórmula: Energia Média × (Valor Tarifa - Valor Fio B) × Percentual</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Valor Locação Terras (25%)</Label>
                    <Input 
                      name="land_rent_value" 
                      type="text" 
                      value={landRentValue}
                      readOnly
                      className="bg-white font-semibold"
                    />
                  </div>
                  <div>
                    <Label>Valor Locação Equipamentos (75%)</Label>
                    <Input 
                      name="equipment_rent_value" 
                      type="text" 
                      value={equipmentRentValue}
                      readOnly
                      className="bg-white font-semibold"
                    />
                  </div>
                  <div>
                    <Label>Dados Bancários</Label>
                    <Input name="bank_details" defaultValue={editingPlant?.bank_details} />
                  </div>
                </div>
              </div>
              )}

              {!isRegistrationOnly && (
              <div>
                <Label>Situação do Rateio</Label>
                <Input name="apportionment_status" defaultValue={editingPlant?.apportionment_status} />
              </div>
              )}

              {/* Plant Address */}
              <div>
                <h3 className="font-semibold mb-2">Endereço da Usina</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Rua</Label>
                    <Input name="plant_street" defaultValue={editingPlant?.plant_street} />
                  </div>
                  <div>
                    <Label>Número</Label>
                    <Input name="plant_number" defaultValue={editingPlant?.plant_number} />
                  </div>
                  <div>
                    <Label>Bairro</Label>
                    <Input name="plant_neighborhood" defaultValue={editingPlant?.plant_neighborhood} />
                  </div>
                  <div>
                    <Label>Complemento</Label>
                    <Input name="plant_complement" defaultValue={editingPlant?.plant_complement} />
                  </div>
                  <div>
                    <Label>Cidade</Label>
                    <Input name="plant_city" defaultValue={editingPlant?.plant_city} />
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Input name="plant_state" defaultValue={editingPlant?.plant_state} />
                  </div>
                  <div>
                    <Label>CEP</Label>
                    <Input name="plant_zip_code" defaultValue={editingPlant?.plant_zip_code} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Número de Matrícula</Label>
                  <Input name="registration_number" defaultValue={editingPlant?.registration_number} />
                </div>
                {!isRegistrationOnly && (<>
                <div>
                  <Label>Data de Assinatura</Label>
                  <Input name="signature_date" type="date" defaultValue={editingPlant?.signature_date?.split('T')[0]} />
                </div>
                <div>
                  <Label>Data de Transferência de Titularidade</Label>
                  <Input
                    name="ownership_transfer_date"
                    type="date"
                    defaultValue={editingPlant?.ownership_transfer_date || ""}
                    data-testid="form-ownership-transfer-date"
                  />
                </div>
                <div>
                  <Label>Primeiro Mês de Compensação</Label>
                  <Input
                    name="first_compensation_month"
                    placeholder="Ex: JAN/2025"
                    defaultValue={editingPlant?.first_compensation_month || ""}
                    data-testid="form-first-compensation-month"
                  />
                </div>
                </>)}
              </div>

              {/* Condições Comerciais (Contrato) */}
              {!isRegistrationOnly && (
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-blue-700 mb-3">Condições Comerciais (Contrato)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Tipo de Pagamento</Label>
                    <select
                      name="payment_type"
                      defaultValue={editingPlant?.payment_type || ""}
                      data-testid="form-payment-type"
                      className="mt-1 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      <option value="Injetada">Injetada</option>
                      <option value="Compensado">Compensada</option>
                    </select>
                  </div>
                  <div>
                    <Label>Custo de Adesão</Label>
                    <select
                      name="adhesion_cost_type"
                      defaultValue={editingPlant?.adhesion_cost_type || ""}
                      data-testid="form-adhesion-cost-type"
                      onChange={(e) => setAdhesionCostType(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      <option value="Com CAC">Com CAC</option>
                      <option value="Sem CAC">Sem CAC</option>
                    </select>
                  </div>
                  {adhesionCostType === "Com CAC" && (
                    <div>
                      <Label>Número de CAC (R$)</Label>
                      <Input
                        name="cac_number"
                        type="number"
                        step="0.01"
                        placeholder="0,00"
                        defaultValue={
                          editingPlant?.cac_number != null ? editingPlant.cac_number : ""
                        }
                        data-testid="form-cac-number"
                      />
                    </div>
                  )}
                </div>
              </div>
              )}
              {isRegistrationOnly && (
                <p className="text-xs text-gray-500 border-t pt-3" data-testid="registration-note">
                  Status na distribuidora, tarifa, Fio B, rateio, datas de assinatura/titularidade, 1º mês de compensação e condições comerciais serão preenchidos pelo usuário Master.
                </p>
              )}

              <DialogFooter>
                {!isRegistrationOnly && (
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Cancelar
                  </Button>
                )}
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingPlant ? "Atualizar" : "Cadastrar"}
                </Button>
              </DialogFooter>
            </form>
  );

  if (isRegistrationOnly) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-6 max-w-5xl mx-auto p-6" data-testid="powerplant-registration-page">
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Cadastro de Usina</h1>
            <p className="text-gray-600">Preencha os dados abaixo para cadastrar uma nova usina</p>
          </div>
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Factory className="w-5 h-5" /> Nova Usina</CardTitle>
            </CardHeader>
            <CardContent>{powerplantForm}</CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (user?.role !== "admin" && !isRegistrationOnly) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">
          <p className="text-gray-600">Acesso restrito a administradores.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Usinas</h1>
            <p className="text-gray-600">Gerenciamento de usinas geradoras</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleDownloadTemplate} className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700" data-testid="download-csv-template-btn">
              <Download className="w-4 h-4 mr-2" />
              Baixar Modelo
            </Button>
            <Button onClick={handleDownloadWordTemplate} className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white" data-testid="download-word-template-btn">
              <FileDown className="w-4 h-4 mr-2" />
              Baixar Modelo Word
            </Button>
            <Button onClick={handleExportCSV} variant="outline" className="bg-white">
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
            <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="bg-white">
              <Upload className="w-4 h-4 mr-2" />
              Importar CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              className="hidden"
            />
            <Button onClick={() => { setEditingPlant(null); resetFormValues(null); setShowDialog(true); }} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Nova Usina
            </Button>
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-4">
              <Search className="w-5 h-5 text-gray-400" />
              <Input
                placeholder="Buscar por nome da usina, proprietário ou nº instalação..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="max-w-md"
              />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Carregando...</div>
            ) : filteredPlants.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma usina encontrada.
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome da Usina</TableHead>
                      <TableHead>Proprietário</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Distribuidora</TableHead>
                      <TableHead>Nº Instalação</TableHead>
                      <TableHead>Potência (kW)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedPlants.map((plant) => (
                      <TableRow key={plant.id}>
                        <TableCell className="font-medium">{plant.plant_name}</TableCell>
                        <TableCell>{plant.owner_name}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                            {plant.registration_type}
                          </span>
                        </TableCell>
                        <TableCell>{plant.distributor}</TableCell>
                        <TableCell className="text-sm text-gray-600">{plant.generator_installation_number}</TableCell>
                        <TableCell>{plant.installed_power} kW</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            plant.distributor_status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                            plant.distributor_status === 'INACTIVE' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {plant.distributor_status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { 
                                setEditingPlant(plant); 
                                resetFormValues(plant);
                                setShowDialog(true); 
                              }}
                            >
                              Editar
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  data-testid={`btn-contract-${plant.id}`}
                                  title="Baixar contratos"
                                  className="text-blue-700 border-blue-200 hover:bg-blue-50 px-2"
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  <span className="text-xs font-semibold">Contratos</span>
                                  <ChevronDown className="w-3 h-3 ml-1" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-80">
                                {CONTRACT_PARTS.map((p) => (
                                  <DropdownMenuSub key={p.key}>
                                    <DropdownMenuSubTrigger data-testid={`contract-part-${p.key}-${plant.id}`} className="text-sm">
                                      {p.label}
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                      <DropdownMenuItem
                                        onClick={() => handleDownloadContract(plant, "pdf", p.key)}
                                        data-testid={`contract-${p.key}-pdf-${plant.id}`}
                                      >
                                        <FileText className="w-4 h-4 mr-2 text-blue-600" /> Baixar PDF
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={() => handleDownloadContract(plant, "docx", p.key)}
                                        data-testid={`contract-${p.key}-docx-${plant.id}`}
                                      >
                                        <FileDown className="w-4 h-4 mr-2 text-indigo-600" /> Baixar Word
                                      </DropdownMenuItem>
                                    </DropdownMenuSubContent>
                                  </DropdownMenuSub>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(plant.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-sm text-gray-600">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Dialog for Add/Edit */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Factory className="w-5 h-5" />
                {editingPlant ? "Editar Usina" : "Nova Usina"}
              </DialogTitle>
              <DialogDescription>
                {editingPlant ? "Atualize os dados da usina" : "Preencha os dados da nova usina"}
              </DialogDescription>
            </DialogHeader>
            {powerplantForm}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
